import React from 'react';
import posthog from 'posthog-js';
import { Dispatch } from 'redux';
import { NavigateFunction } from 'react-router';

import { Auth, I18n } from 'aws-amplify';
import { Heading, useTheme } from '@aws-amplify/ui-react';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import { Buffer } from 'buffer';
import Compare from '../Views/Compare';
import LocationConfig from '../Views/LocationConfig';
import SensorConfig from '../Views/SensorConfig';
import ProvisionSensor from '../Views/ProvisionSensor';
import CalendarView from '../Views/CalendarView';
import UserProfile from '../Views/UserProfile';
import AppInstallSensor from '../Views/App/AppInstallSensor';
import AppAdminSensor from '../Views/App/AppAdminSensor';
import ListViewDashboard from '../Views/ListViewDashboard';
import Dashboard from '../Views/Dashboard';
import { TempUser } from '../state/types';
import {
  clearReduxStore,
  setBleWebViewUrl,
  setShowNavDrawer,
  setTempUser,
  setUserPublic,
} from '../state/actions';
import { StorageTypes, clearState } from '../utils/persistentState';
import { ActionTypes } from '../state/actionTypes';
import OfflineView from '../Views/OfflineView';
import Report from '../Views/Report';

dayjs.extend(calendar);

export const appSwitchBtnHandler = (dispatch: Dispatch<ActionTypes>) => {
  dispatch(setShowNavDrawer(false));
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  window.ReactNativeWebView?.postMessage(JSON.stringify({ screen: 'home' }));
  dispatch(setBleWebViewUrl('')); // For special use case if sensor details are changed in app sensor info
};

export const handleLogout = (
  tempUser: TempUser | null,
  dispatch: Dispatch<ActionTypes>,
  resetUrl?: NavigateFunction
) => {
  if (tempUser) {
    dispatch(setTempUser(null));
  } else {
    Auth.signOut();
    dispatch(setUserPublic(null));
  }
  // reset url to root, which removes tempCredential from url as well
  if (resetUrl) resetUrl('/');

  // Clear other areas of store that might be problematic
  // TODO Should really remove all sensor/location data.
  clearState(StorageTypes.CurrentLocation);
  clearState(StorageTypes.SelectedPlotSensors);
  dispatch(clearReduxStore());
  posthog.reset();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  window.ReactNativeWebView?.postMessage(JSON.stringify({ userInfo: null }));
};

export const tempUserExpiryStr = (expTime: number): string => {
  const expStr = dayjs.unix(expTime).calendar(null, {
    sameDay: '[today at] HH:mm', // The same day ( today at 14:30 )
    nextDay: '[tomorrow]', // The next day ( tomorrow )
    nextWeek: 'dddd', // The next week ( Sunday )
    lastDay: '[yesterday]', // The day before ( yesterday )
    lastWeek: '[last] dddd', // Last week ( last Monday )
    sameElse: 'DD/MM/YYYY', // Everything else ( 7/10/2011 )
  });
  return `Temporary (Access expires ${expStr})`;
};

export const loginComponents = {
  SignIn: {
    Header() {
      const { tokens } = useTheme();
      return (
        <Heading padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`} level={3}>
          Sign in to the LightFi portal
        </Heading>
      );
    },
  },
};

export const parseJwt = (token: string) => {
  const base64Url = token.split('.')[1];
  return JSON.parse(Buffer.from(base64Url, 'base64').toString('utf8'));
};

I18n.putVocabulariesForLanguage('en', {
  'Reset your password': 'Reset your LightFi password',
  'Forgot your password?': 'Reset Password',
});

export const formFields = {
  signIn: {
    username: {
      labelHidden: false,
      placeholder: 'Enter your email address',
      isRequired: true,
      label: 'Email Address *',
    },
    password: {
      labelHidden: false,
      placeholder: 'Enter Your password',
      isRequired: true,
      label: 'Password *',
    },
  },
  resetPassword: {
    username: {
      labelHidden: false,
      placeholder: 'Enter your email address',
      isRequired: true,
      label: 'Email Address *',
    },
  },
};

export const routes = [
  {
    id: 1,
    url: '/compare',
    helmetTitle: 'Compare | LightFi',
    component: Compare,
  },
  {
    id: 2,
    url: '/location/:locationId',
    helmetTitle: 'Location Config | LightFi',
    component: LocationConfig,
  },
  {
    id: 3,
    url: '/location',
    helmetTitle: 'Location Config | LightFi',
    component: LocationConfig,
  },
  {
    id: 4,
    url: '/sensors/:sensorId',
    helmetTitle: 'Sensor Config | LightFi',
    component: SensorConfig,
  },
  {
    id: 5,
    url: '/provision/:sensorId',
    helmetTitle: 'Provision | LightFi',
    component: ProvisionSensor,
  },
  {
    id: 6,
    url: '/userProfile',
    helmetTitle: 'User Profile | LightFi',
    component: UserProfile,
  },
  {
    id: 7,
    url: '/calendarView',
    helmetTitle: 'Calendar View | LightFi',
    component: CalendarView,
  },
  {
    id: 8,
    url: '/install/:sensorId',
    helmetTitle: 'Install Sensor | LightFi',
    component: AppInstallSensor,
  },
  {
    id: 9,
    url: '/install',
    helmetTitle: 'Install Sensor | LightFi',
    component: AppInstallSensor,
  },
  {
    id: 10,
    url: '/appAdmin/:sensorId',
    helmetTitle: 'App Admin Sensor | LightFi',
    component: AppAdminSensor,
  },
  {
    id: 11,
    url: '/list',
    helmetTitle: 'List View Dashboard | LightFi',
    component: ListViewDashboard,
  },

  // keeping this for the smooth transition, remember to remove in future and clear all oldDashboard/appSource menu component
  {
    id: 12,
    url: '/appSourceMenu',
    helmetTitle: 'Dashboard | LightFi',
    component: Dashboard,
  },
  {
    id: 13,
    url: '/appDashboard',
    helmetTitle: 'App Dashboard | LightFi',
    component: Dashboard,
  },
  {
    id: 14,
    url: '/appDashboard/:sensorId',
    helmetTitle: 'Dashboard | LightFi',
    component: Dashboard,
  },
  {
    id: 15,
    url: '/offline',
    helmetTitle: 'Dashboard | LightFi',
    component: OfflineView,
  },
  {
    id: 16,
    url: '/report',
    helmetTitle: 'Report View | LightFi',
    component: Report,
  },
  {
    id: 17,
    url: '/',
    helmetTitle: 'Dashboard | LightFi',
    component: Dashboard,
  },
];
